import type { ExportActionId } from './exportActions';

export type ReportCatalogId = 'breakdown-summary' | 'schedule' | 'dood' | 'vfx';
export type ReportTargetView = 'reports' | 'scheduling' | 'dood' | 'vfx';

export interface ReportCatalogEntry {
    readonly id: ReportCatalogId;
    readonly label: string;
    readonly description: string;
    readonly targetView: ReportTargetView;
    readonly exportActionIds: readonly ExportActionId[];
}

const actionIds = (...ids: ExportActionId[]): readonly ExportActionId[] => Object.freeze(ids);

/**
 * Discovery metadata only. Each target view retains its own canonical report
 * model; this catalog deliberately does not combine their calculations.
 */
export const REPORT_CATALOG = Object.freeze([
    Object.freeze({
        id: 'breakdown-summary',
        label: 'Breakdown Summary',
        description: 'Scene totals, timings, categories, and element cross-reference.',
        targetView: 'reports',
        exportActionIds: actionIds(
            'report-print',
            'breakdown-pdf',
            'breakdown-xlsx',
            'breakdown-csv',
        ),
    }),
    Object.freeze({
        id: 'schedule',
        label: 'Stripboard Schedule',
        description: 'Canonical shooting order, shooting days, locations, cast, and timing.',
        targetView: 'scheduling',
        exportActionIds: actionIds(
            'stripboard-xlsx',
            'stripboard-csv',
            'stripboard-pdf',
        ),
    }),
    Object.freeze({
        id: 'dood',
        label: 'DOOD',
        description: 'Day out of days start, work, hold, and finish statuses.',
        targetView: 'dood',
        exportActionIds: actionIds('dood-xlsx', 'dood-print'),
    }),
    Object.freeze({
        id: 'vfx',
        label: 'VFX Breakdown',
        description: 'VFX scenes, elements, categories, and production complexity.',
        targetView: 'vfx',
        exportActionIds: actionIds('vfx-xlsx'),
    }),
] satisfies readonly ReportCatalogEntry[]);

export const getReportCatalogEntry = (id: ReportCatalogId): ReportCatalogEntry => {
    const report = REPORT_CATALOG.find(candidate => candidate.id === id);
    if (!report) throw new Error(`Unknown report catalog id: ${id}`);
    return report;
};
